import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader, EmptyState, LoadingShell, Stat } from '@/components/ui-bits';
import { Briefcase, Plus, X, Save, Trash2, LayoutGrid, List, GripVertical, Search } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { v: 'open', l: 'Abierto', color: '#3b82f6' },
  { v: 'negotiation', l: 'Negociación', color: '#f59e0b' },
  { v: 'won', l: 'Ganado', color: '#10b981' },
  { v: 'lost', l: 'Perdido', color: '#ef4444' },
];

export default function DealsPage() {
  const [items, setItems] = useState([]);
  const [weighted, setWeighted] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('');
  const [companies, setCompanies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState('kanban'); // kanban | table
  const [kanban, setKanban] = useState(null);
  const [drag, setDrag] = useState(null);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (view === 'kanban') {
        const params = new URLSearchParams();
        if (q.trim()) params.set('q', q.trim());
        const { data } = await api.get(`/deals/kanban?${params}`);
        setKanban(data);
        const all = Object.values(data.deals_by_stage || {}).flat();
        setItems(all);
        const w = all
          .filter((d) => d.stage === 'open' || d.stage === 'negotiation')
          .reduce((s, d) => s + (Number(d.amount) || 0) * ((Number(d.probability) || 50) / 100), 0);
        setWeighted(w);
      } else {
        const params = new URLSearchParams({ limit: '500' });
        if (stage) params.set('stage', stage);
        const { data } = await api.get(`/deals?${params}`);
        setItems(data.deals || []);
        setWeighted(data.weighted_pipeline || 0);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  }, [stage, view, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/companies?limit=500').then((r) => setCompanies(r.data.companies || [])).catch(() => {});
  }, []);

  const save = async () => {
    try {
      const payload = {
        ...editing,
        amount: Number(editing.amount) || 0,
        probability: Number(editing.probability) || 50,
      };
      if (editing.id) {
        await api.patch(`/deals/${editing.id}`, payload);
        toast.success('Deal actualizado');
      } else {
        await api.post('/deals', payload);
        toast.success('Deal creado');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const del = async (d) => {
    if (!confirm('¿Eliminar deal?')) return;
    try {
      await api.delete(`/deals/${d.id}`);
      toast.success('Eliminado');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const moveDeal = async (dealId, newStage) => {
    try {
      await api.patch(`/deals/${dealId}`, { stage: newStage });
      toast.success('Deal movido');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || '—';

  const stats = useMemo(() => {
    const won = items.filter((d) => d.stage === 'won');
    const lost = items.filter((d) => d.stage === 'lost');
    return {
      wonAmt: won.reduce((s, d) => s + (Number(d.amount) || 0), 0),
      wonN: won.length,
      lostN: lost.length,
    };
  }, [items]);

  return (
    <div>
      <PageHeader
        title="Deals / Negocios"
        description="Kanban por etapa o tabla. Arrastra para cambiar etapa."
        actions={(
          <div className="flex gap-2">
            <button
              type="button"
              className={`btn-ghost ${view === 'kanban' ? 'bg-accent' : ''}`}
              onClick={() => setView('kanban')}
              title="Kanban"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={`btn-ghost ${view === 'table' ? 'bg-accent' : ''}`}
              onClick={() => setView('table')}
              title="Tabla"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              className="btn-primary"
              onClick={() => setEditing({
                name: '', amount: 0, currency: 'MXN', stage: 'open', probability: 50, company_id: '', notes: '',
              })}
            >
              <Plus className="h-4 w-4" /> Nuevo deal
            </button>
          </div>
        )}
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Deals" value={items.length} />
        <Stat label="Pipeline ponderado" value={`$${Number(weighted).toLocaleString('es-MX')}`} hint="amount × probabilidad" />
        <Stat label="Ganados $" value={`$${stats.wonAmt.toLocaleString('es-MX')}`} hint={`${stats.wonN} deals`} />
        <Stat label="Perdidos" value={stats.lostN} />
      </div>

      {view === 'kanban' ? (
        <>
          <div className="surface p-3 mb-4">
            <label className="relative block">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar deal…" className="input pl-9" />
            </label>
          </div>
          {loading || !kanban ? <LoadingShell /> : (
            <div className="flex gap-3 overflow-x-auto pb-3">
              {(kanban.stages || STAGES.map((s) => ({ id: s.v, name: s.l, color: s.color }))).map((s) => {
                const col = (kanban.deals_by_stage || {})[s.id] || [];
                const tot = (kanban.totals || {})[s.id] || {};
                return (
                  <div
                    key={s.id}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => drag && moveDeal(drag, s.id)}
                    className="w-[300px] shrink-0"
                  >
                    <div className="flex items-center justify-between mb-2 px-3 py-2 rounded-[10px] bg-secondary/60 border border-border">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                        <span className="font-medium text-sm">{s.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="chip text-[10px]">{tot.count ?? col.length}</div>
                        <div className="text-[10px] text-muted-foreground tabular-nums">
                          ${(tot.amount || 0).toLocaleString('es-MX')}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 min-h-[200px]">
                      {col.map((d) => (
                        <div
                          key={d.id}
                          draggable
                          onDragStart={() => setDrag(d.id)}
                          onDragEnd={() => setDrag(null)}
                          className="rounded-[12px] border border-border bg-card p-3 shadow-sm hover:border-primary/40 cursor-grab active:cursor-grabbing"
                        >
                          <div className="flex items-start justify-between gap-1">
                            <button type="button" className="font-medium text-sm text-left hover:underline" onClick={() => setEditing(d)}>
                              {d.name}
                            </button>
                            <GripVertical className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </div>
                          <div className="text-sm font-semibold tabular-nums mt-1">
                            ${Number(d.amount || 0).toLocaleString('es-MX')}
                            <span className="text-xs font-normal text-muted-foreground ml-1">{d.probability ?? 0}%</span>
                          </div>
                          {(d.company_name || d.company_id) && (
                            <div className="text-xs text-muted-foreground mt-1 truncate">
                              {d.company_name || companyName(d.company_id)}
                            </div>
                          )}
                          {d.lead_id && (
                            <Link to={`/app/leads/${d.lead_id}`} className="text-[11px] text-primary hover:underline mt-1 inline-block" onClick={(e) => e.stopPropagation()}>
                              Ver lead
                            </Link>
                          )}
                        </div>
                      ))}
                      {col.length === 0 && (
                        <div className="text-xs text-muted-foreground text-center py-8 border border-dashed border-border rounded-[10px]">
                          Arrastra aquí
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mb-3">
            <select value={stage} onChange={(e) => setStage(e.target.value)} className="input w-auto">
              <option value="">Todas las etapas</option>
              {STAGES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          {loading ? <LoadingShell /> : items.length === 0 ? (
            <EmptyState icon={Briefcase} title="Sin deals" description="Crea un negocio y vincúlalo a una empresa o lead." />
          ) : (
            <div className="surface overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="px-3 py-2">Nombre</th>
                    <th className="px-3 py-2">Empresa</th>
                    <th className="px-3 py-2 text-right">Monto</th>
                    <th className="px-3 py-2 text-right">Prob. %</th>
                    <th className="px-3 py-2">Etapa</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((d) => (
                    <tr key={d.id} className="border-b border-border/60 hover:bg-accent/40">
                      <td className="px-3 py-2 font-medium">{d.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{companyName(d.company_id)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">${Number(d.amount || 0).toLocaleString('es-MX')}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{d.probability ?? 0}%</td>
                      <td className="px-3 py-2">
                        <span className="chip text-xs">{STAGES.find((s) => s.v === d.stage)?.l || d.stage}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button className="btn-ghost h-8 px-2 text-xs" onClick={() => setEditing(d)}>Editar</button>
                        <button className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]" onClick={() => del(d)}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="surface w-full max-w-lg p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between">
              <h2 className="font-display text-lg font-semibold">{editing.id ? 'Editar deal' : 'Nuevo deal'}</h2>
              <button className="btn-ghost h-8 w-8 p-0" onClick={() => setEditing(null)}><X className="h-4 w-4" /></button>
            </div>
            <label className="block text-xs">Nombre *<input className="input mt-1" value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
            <label className="block text-xs">Empresa
              <select className="input mt-1" value={editing.company_id || ''} onChange={(e) => setEditing({ ...editing, company_id: e.target.value || null })}>
                <option value="">—</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <label className="block text-xs">Monto<input type="number" className="input mt-1" value={editing.amount ?? 0} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} /></label>
              <label className="block text-xs">Prob. %<input type="number" min={0} max={100} className="input mt-1" value={editing.probability ?? 50} onChange={(e) => setEditing({ ...editing, probability: e.target.value })} /></label>
              <label className="block text-xs">Etapa
                <select className="input mt-1" value={editing.stage || 'open'} onChange={(e) => setEditing({ ...editing, stage: e.target.value })}>
                  {STAGES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </label>
            </div>
            {editing.stage === 'lost' && (
              <label className="block text-xs">Motivo pérdida<input className="input mt-1" value={editing.lost_reason || ''} onChange={(e) => setEditing({ ...editing, lost_reason: e.target.value })} /></label>
            )}
            <label className="block text-xs">Cierre esperado<input type="date" className="input mt-1" value={(editing.expected_close_at || '').slice(0, 10)} onChange={(e) => setEditing({ ...editing, expected_close_at: e.target.value || null })} /></label>
            <label className="block text-xs">Notas<textarea className="input mt-1" value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></label>
            <div className="flex justify-end gap-2">
              {editing.id && (
                <button className="btn-ghost text-[hsl(var(--danger))] mr-auto" onClick={() => { del(editing); setEditing(null); }}>Eliminar</button>
              )}
              <button className="btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
              <button className="btn-primary" disabled={!editing.name?.trim()} onClick={save}><Save className="h-4 w-4" /> Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
